import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'
import type { Document as RichTextDocument } from '@contentful/rich-text-types'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Footer from '@segment/templates/Footer'
import Navigation from '@segment/templates/Navigation'

import GettingStarted from '@segment/templates/GettingStarted'

import { useMoreCustomersQuery, useStoryQuery } from '@segment/api/routes/customers/queries'

import verifyDataExists from '@segment/utils/verifyDataExists'

import useRichTextTableOfContent from '@segment/utils/useRichTextTableOfContent'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import Layout from '@segment/next-shared/layout'

import Hero from './components/Hero'
import Results from './components/Results'
import Content from './components/Content'
import TheBigWin from './components/TheBigWin'
import Video from './components/Video'

import MoreCustomers from './components/MoreCustomers'

export interface CustomerStoryViewProps {
  storySlug: string
  random: number
  amount: number
}

const CustomerStoryView: FC<PropsWithChildren<CustomerStoryViewProps>> = ({ storySlug, random, amount }) => {
  const theme = useTheme()
  const { data } = useStoryQuery({ args: [storySlug] })
  const pageConfig = usePageConfig()
  const { data: moreCustomers } = useMoreCustomersQuery({ args: [{ slug: storySlug, random, amount }] })

  if (!data) throw new Error(`Story data is missing. Make sure it's preloaded`)

  if (!verifyDataExists(data.fields, [`socialTitle`, `title`, `matchaLogo`, `location`, `content`]))
    throw new Error(`Required fields are missing. Check log for details.`)

  const tableOfContentState = useRichTextTableOfContent(data.fields.content as RichTextDocument)

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle={data.fields.socialTitle}
        pageDescription={data.fields.socialDescription}
        themeColor={theme.palette.brand.darkAcai[700]}
        bodyBackground={theme.palette.neutrals.white[100]}
        pageOG={data.fields.socialImage?.fields?.file?.url}
      >
        <Navigation />
        <Hero
          title={data.fields.title}
          featuredImage={data.fields.featuredImage}
          heroCtaText={data.fields.heroCtaText}
          heroCtaUrl={data.fields.heroCtaUrl}
          useCase={data.fields.categoryUseCase}
          industry={data.fields.categoryIndustry}
          location={data.fields.location}
          region={data.fields.categoryRegion}
          products={data.fields.categoryProducts}
        />
        <Main>
          <Wrapper>
            <Sidebar>
              <Results
                data={data.fields.results}
                demoUrl={getAbsoluteUrl(
                  `/demo/?utm_medium=referral&utm_source=customer-story&utm_campaign=all_pmm_customer-story_&utm_content=demo-request`,
                  pageConfig
                )}
                logo={data.fields.matchaLogo?.fields.logoColor}
              />
            </Sidebar>
            <ContentWrapper>
              {data.fields.videoUrl && <Video url={data.fields.videoUrl} />}
              {data.fields.bigWin && <TheBigWin bigWin={data.fields.bigWin} />}
              <Content data={data.fields.content} tableOfContentState={tableOfContentState} />
            </ContentWrapper>
          </Wrapper>
          {Boolean(moreCustomers?.length) && <MoreCustomers entries={moreCustomers} />}
          <GettingStarted />
        </Main>
      </Layout>
      <Footer />
    </AppearanceProvider>
  )
}

const Wrapper = styled.div`
  display: grid;
  max-width: 1280px;
  padding: 0 24px;
  margin: 44px auto;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    margin: 82px auto;
    grid-template-columns: 2fr 8fr;
    gap: 60px;
    justify-content: space-between;
  }

  ${({ theme }) => theme.breakpoints.up(`xxl`)} {
    gap: 200px;
  }
`

const Sidebar = styled.aside`
  display: grid;
  gap: 48px;
  margin-bottom: 24px;
  grid-template-rows: auto 1fr;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 210px;
  }
`

const ContentWrapper = styled.div`
  max-width: 844px;
`

const Main = styled.main``

export default CustomerStoryView
